import React, { useEffect, useState } from 'react';

// Import Polotno and third-party libraries
import QRCode from 'qrcode';
import { observer } from 'mobx-react-lite';
import { SectionTab } from 'polotno/side-panel';
import type { StoreType } from 'polotno/model/store';

import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../redux/store';
import { failure, success } from '../../../redux/actions/snackbarActions';
import {
  setQrUrl,
  setUtmSource,
  setUtmMedium,
  setUtmCampaignName,
  setQrSendEmail,
  setQrNotificationEmail,
  setCustomUtms,
  clearQrFields,
  setIsQR,
  setQrDialog,
  closeQrDialog,
} from '../../../redux/actions/customQRCodeActions';
import { uploadFile } from '../../../redux/actions/templateActions';

// Utils
import { MESSAGES } from '../../../utils/message';
import { copyToClipboard, validURL } from '../../../utils/helper';
import {
  DISALLOWED_DOMAINS,
  MERGE_UTM_PARAMS,
  emojiRegex,
} from '../../../utils/constants';
import {
  formatTrackingPhoneNumber,
  getRawTrackingPhoneToken,
  getTrackingPhoneToken,
  registerTrackingPhoneToken,
} from '../../../utils/template-builder';
import { getItem, setItem } from '../../../utils/local-storage';

//Components
import GeneralSelect from '../../../components/GenericUIBlocks/GeneralSelect';
import QRCodeModal from './V2/QRCodeModal';
import DialogV2 from '../../../components/GenericUIBlocks/Dialog/V2';
import Tabs from '../../../components/GenericUIBlocks/Tabs';

// UI Components
import Input from '../../../components/GenericUIBlocks/Input';
import Typography from '../../GenericUIBlocks/Typography';
import GeneralTooltip from '../../GenericUIBlocks/GeneralTooltip';
import Button from '../../GenericUIBlocks/Button';

// Icons
import CustomQRIcon from '../../../assets/images/templates/custom-qr-section-icon';
import TrackingPhoneIcon from '../../../assets/images/templates/tracking-phone-icon';
import QRCodeIcon from '../../../assets/images/templates/qr-code';
import CheckboxIcon from '../../../assets/images/input/checkbox-icon';
import InfoIconFill from '../../../assets/images/templates/info-icon-fill';
import ContentCopyIcon from '../../../assets/images/templates/content-copy-icon';
import SearchIcon from '../../../assets/images/input/search';

// @ts-ignore
import Edit from '../../../assets/images/templates/edit.svg';
// @ts-ignore
import Archive from '../../../assets/images/templates/archive.svg';

// @ts-ignore
import ConfirmCloseIcon from '../../../assets/images/modal-icons/confirm-close-icon';
// @ts-ignore
import Actions from '../../../assets/images/templates/actions.svg'

// styles
import './styles.scss';

interface CustomQRProps {
  store: StoreType;
  allowSenderFields: any;
  allowPropertyFields: any;
  excludedFields: any;
  showQrScanNotificationFields?: boolean;
  defaultQrNotificationEmail?: string;
  qrScanNotificationEnabled?: boolean;
  currentTheme?: string | null | undefined;
  onGetQRCodes?: (payload: any) => Promise<any>;
  onDeleteQRCodes?: (id: string | number) => Promise<void>;
  onUploadQRCode?: (payload: any) => Promise<any>;
  onEditQRCode?: (payload: any) => Promise<any>;
  onGetTrackingPhoneNumbers?: (payload: any) => Promise<any>;
  onLookupTrackingPhoneNumbers?: (payload: any) => Promise<any>;
  onCreateTrackingPhoneNumber?: (payload: any) => Promise<any>;
}

const cancelDialogStylesV2 = {
  maxWidth: '567px',
  minHeight: 'auto',
  padding: '40px',
};

const trackingTabs = [
  { id: 'qr', label: 'QR', className: 'tracking-tab-qr' },
  { id: 'phone', label: 'Phone' },
];

const trackingTabStorageKey = 'trackingSelectedTab';

const getStoredTrackingTab = () => {
  try {
    const tab = getItem(trackingTabStorageKey);
    return trackingTabs.find((trackingTab) => trackingTab.id === JSON.parse(tab || '{}')?.id) || trackingTabs[0];
  } catch {
    return trackingTabs[0];
  }
};

// define the new custom section
const CustomQRCode = {
  name: 'Tracking',
  Tab: (props: any) => (
    <SectionTab name="Tracking" {...props} iconSize={20}>
      <CustomQRIcon />
    </SectionTab>
  ),

  // we need observer to update component automatically on any store changes
  Panel: observer(
    ({
      store,
      allowSenderFields,
      allowPropertyFields,
      excludedFields,
      currentTheme,
      onGetQRCodes,
      onDeleteQRCodes,
      onUploadQRCode,
      onEditQRCode,
      onGetTrackingPhoneNumbers,
      onLookupTrackingPhoneNumbers,
      onCreateTrackingPhoneNumber,
      showQrScanNotificationFields = false,
      defaultQrNotificationEmail = '',
      qrScanNotificationEnabled = true,
    }: CustomQRProps) => {
      // V2 State for API-driven functionality
      const [selectedTrackingTab, setSelectedTrackingTab] = useState(getStoredTrackingTab);
      const [qrCodes, setQrCodes] = useState<any[]>([]);
      const [isLoading, setIsLoading] = useState(false);
      const [isLoadingMore, setIsLoadingMore] = useState(false);
      const [currentPage, setCurrentPage] = useState(1);
      const [hasMore, setHasMore] = useState(true);
      const [trackingPhoneNumbers, setTrackingPhoneNumbers] = useState<any[]>([]);
      const [isPhoneLoading, setIsPhoneLoading] = useState(false);
      const [isPhoneLoadingMore, setIsPhoneLoadingMore] = useState(false);
      const [phoneCurrentPage, setPhoneCurrentPage] = useState(1);
      const [phoneHasMore, setPhoneHasMore] = useState(true);
      const [isTrackingPhoneDialogOpen, setIsTrackingPhoneDialogOpen] = useState(false);
      const [trackingPhoneForm, setTrackingPhoneForm] = useState({
        phoneName: '',
        callForwardingNumber: '',
        smsForwardingEmail: '',
        areaCode: '',
        selectedNumber: '',
      });
      const [trackingPhoneErrors, setTrackingPhoneErrors] = useState<Record<string, string>>({});
      const [availableTrackingPhoneNumbers, setAvailableTrackingPhoneNumbers] = useState<any[]>([]);
      const [hasSearchedTrackingPhoneNumbers, setHasSearchedTrackingPhoneNumbers] = useState(false);
      const [isTrackingPhoneLookupLoading, setIsTrackingPhoneLookupLoading] = useState(false);
      const [isTrackingPhoneCreateLoading, setIsTrackingPhoneCreateLoading] = useState(false);
      const [activeDropdown, setActiveDropdown] = useState<
        string | number | null
      >(null);
      const [isEditing, setIsEditing] = useState(false);
      const [selectedQR, setSelectedQR] = useState<any>(null);
      const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
      const [isLoadingDelete, setIsLoadingDelete] = useState(false);
      const [saveQrLoading, setSaveQrLoading] = useState(false);
      const isQrScanNotificationAvailable =
        showQrScanNotificationFields && qrScanNotificationEnabled;

      const [isActionsOpen, setIsActionsOpen] = useState<
        string | number | null
      >(null);
      const [actionsDropdownUpward, setActionsDropdownUpward] = useState(false);

      const qrCodesGridRef = React.useRef<HTMLDivElement>(null);
      const trackingPhoneNumbersRef = React.useRef<HTMLDivElement>(null);
      const availableTrackingPhoneNumbersRef = React.useRef<HTMLDivElement>(null);
      const qrCodesLoadedRef = React.useRef(false);
      const trackingPhoneNumbersLoadedRef = React.useRef(false);
      const dropdownRefs = React.useRef<Map<string | number, HTMLDivElement>>(
        new Map()
      );
      const actionsRefs = React.useRef<Map<string | number, HTMLDivElement>>(
        new Map()
      );
      const loadingTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
      const isLoadingRef = React.useRef(false);
      const phoneLoadingTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
      const isPhoneLoadingRef = React.useRef(false);

      const handleTrackingTabChange = (tab: any) => {
        setItem(trackingTabStorageKey, JSON.stringify(tab || trackingTabs[0]));
        setSelectedTrackingTab(tab);
      };

      useEffect(() => {
        if (!hasSearchedTrackingPhoneNumbers || availableTrackingPhoneNumbers.length === 0) {
          return undefined;
        }

        const animationFrame = window.requestAnimationFrame(() => {
          availableTrackingPhoneNumbersRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          });
        });

        return () => window.cancelAnimationFrame(animationFrame);
      }, [hasSearchedTrackingPhoneNumbers, availableTrackingPhoneNumbers.length]);

      const noteStyles = {
        fontWeight: '400',
        fontSize: '12px',
        color: '#000',
        width: '100%',
        maxWidth: '245px',
        margin: '16px 0 0',
      };

      const trackingPhoneNoteStyles = {
        ...noteStyles,
        margin: '16px 0 12px',
      };

      const itemStyle = {
        margin: 0,
        fontSize: '12px',
        fontWeight: '300',
      };

      const iconButtonStyles = {
        backgroundColor: 'transparent',
      };
      const trimmedTrackingPhoneAreaCode = trackingPhoneForm.areaCode.trim();
      const canFindTrackingPhoneNumbers = /^\d{3,}$/.test(trimmedTrackingPhoneAreaCode);

      const dialog = useSelector((state: RootState) => state.customQRCode.dialog);
      const url = useSelector((state: RootState) => state.customQRCode.url);
      const utmSource = useSelector(
        (state: RootState) => state.customQRCode.utmSource
      );
      const utmMedium = useSelector(
        (state: RootState) => state.customQRCode.utmMedium
      );
      const utmCampaignName = useSelector(
        (state: RootState) => state.customQRCode.utmCampaignName
      );
      const sendEmail = useSelector(
        (state: RootState) => state.customQRCode.sendEmail
      );
      const notificationEmail = useSelector(
        (state: RootState) => state.customQRCode.notificationEmail
      );
      const customUtms = useSelector(
        (state: RootState) => state.customQRCode.customUtms
      );
      const isQR = useSelector((state: RootState) => state.customQRCode.isQR);

      const dispatch: AppDispatch = useDispatch();

      const handleDialogChange = (model = '', shouldPrefillNotificationEmail = true) => {
        if (dialog.open) {
          dispatch(closeQrDialog());
        } else {
          if (
            shouldPrefillNotificationEmail &&
            isQrScanNotificationAvailable &&
            !notificationEmail
          ) {
            dispatch(setQrNotificationEmail(defaultQrNotificationEmail));
          }
          dispatch(setQrDialog(true, model));
        }
      };

      const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
      };

      const handleOpenDeleteDialog = () => {
        setOpenDeleteDialog(true);
      };

      const handleDialogClose = (model = '') => {
        dispatch(closeQrDialog());
        setIsEditing(false);
        dispatch(setQrUrl(''));
        dispatch(setUtmSource('direct mail'));
        dispatch(setUtmMedium('QR Code'));
        dispatch(setUtmCampaignName(''));
        dispatch(setQrSendEmail(false));
        dispatch(setQrNotificationEmail(''));
        dispatch(setCustomUtms({}));
      };

      const defaultFields = useSelector(
        (state: RootState) => state.templates.defaultDynamicFields
      );

      const defaultSenderFields = useSelector(
        (state: RootState) => state.templates.defaultSenderFields
      
      );

      const customFields = useSelector(
        (state: RootState) => state.customFields.customFields
      );

      const customFieldsV2 = useSelector(
        (state: RootState) => state.customFields.customFieldsV2
      ) as Record<string, any>;

      const defaultPropertyFields = useSelector(
        (state: RootState) => state.templates.defaultPropertyFields
      );

      useEffect(() => {
        if (!qrScanNotificationEnabled && sendEmail) {
          dispatch(setQrSendEmail(false));
          dispatch(setQrNotificationEmail(''));
        }
      }, [dispatch, qrScanNotificationEnabled, sendEmail]);

      const handleQrScanNotificationToggle = () => {
        if (!qrScanNotificationEnabled) return;

        const checked = !sendEmail;
        dispatch(setQrSendEmail(checked));
        if (checked && !notificationEmail) {
          dispatch(setQrNotificationEmail(defaultQrNotificationEmail));
        } else if (!checked) {
          dispatch(setQrNotificationEmail(''));
        }
      };

      const excludedLabels = ['utm_c_first_name c_last_name'];

      let flattenedFieldsV2 = [];
      if (customFieldsV2.length > 0) {
        flattenedFieldsV2 = customFieldsV2?.flatMap(
          (section: { fields: any }) => section.fields
        );
      }

      const allFields = [
        ...defaultFields,
        ...customFields,
        ...flattenedFieldsV2,
        ...(allowSenderFields ? defaultSenderFields : []),
        ...(allowPropertyFields ? defaultPropertyFields : []),
        ...(allowPropertyFields
          ? [
            {
              value: 'ROS.PROPERTY_OFFER',
              key: '{{ROS.PROPERTY_OFFER}}',
              defaultValue: '$123,456.00',
            },
          ]
          : []),
      ].filter(({ key }) => !excludedFields?.includes(key));

      const utmFields = allFields
        .map(({ key }) => ({
          label: `utm_${key?.toLowerCase().replaceAll('.', '_').replaceAll(/[{}]/g, '')}`,
        }))
        .filter((utmField) => !excludedLabels.includes(utmField.label));

      const utms = ['custom_utm_1', 'custom_utm_2', 'custom_utm_3'];

      const el = store.selectedElements[0];

      const clearQRFields = () => {
        store.selectElements([]);
        dispatch(clearQrFields());
      };

      const appendUtmParameters = (utmField: string, defaultValue: string) => {
        let result = '';
        const field = MERGE_UTM_PARAMS.find((field) => field.key === utmField);
        if (field) {
          result = `${field.value}=${defaultValue}`;
        }
        return result;
      };

      const validateQRCode = () => {
        const trimmedNotificationEmail = notificationEmail.trim();
        const validations = [
          { value: utmSource, label: 'UTM Source' },
          { value: utmMedium, label: 'UTM Medium' },
          { value: utmCampaignName, label: 'UTM Campaign' },
        ];

        for (const { value, label } of validations) {
          if (value.length >= 150) {
            dispatch(failure(`${label} must be less than 150 characters`));
            return false;
          }
          if (emojiRegex.test(value)) {
            dispatch(failure(`Emoji are not allowed in ${label}`));
            return false;
          }
        }

        if (isQrScanNotificationAvailable && sendEmail) {
          if (!trimmedNotificationEmail) {
            dispatch(failure(MESSAGES.TEMPLATE.QR_SECTION.NOTIFICATION_EMAIL_REQUIRED));
            return false;
          }
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedNotificationEmail)) {
            dispatch(failure(MESSAGES.TEMPLATE.QR_SECTION.NOTIFICATION_EMAIL_INVALID));
            return false;
          }
        }

        return true;
      };

      const containsDisallowedDomains = (str: string) => {
        return DISALLOWED_DOMAINS.some((substring) => str.includes(substring));
      };

      const getQrNotificationPayload = (existingFields?: any) => {
        if (showQrScanNotificationFields) {
          const effectiveSendEmail = qrScanNotificationEnabled && sendEmail;

          return {
            sendEmail: effectiveSendEmail,
            notificationEmail: effectiveSendEmail ? notificationEmail.trim() : null,
          };
        }

        if (
          !existingFields ||
          (existingFields.sendEmail === undefined && existingFields.notificationEmail === undefined)
        ) {
          return {};
        }

        const existingSendEmail = Boolean(existingFields.sendEmail);
        return {
          sendEmail: existingSendEmail,
          notificationEmail: existingSendEmail ? existingFields.notificationEmail ?? null : null,
        };
      };

      // create svg image for QR code for input text
      const getQR = (text: string) => {
        return new Promise<string>((resolve, reject) => {
          QRCode.toDataURL(
            text || 'no-data',
            {
              type: 'image/png',
              margin: 0,
              color: {
                dark: '#000000',
                light: '#0000', // transparent background
              },
              scale: 10, // increase for higher resolution
            },
            (err: any, url: string) => {
              if (err) return reject(err);
              resolve(url); // returns base64 PNG image string
            }
          );
        });
      };

      // Converts base64 data URL to File
      const base64ToFile = (base64: string, filename: string): File => {
        const arr = base64.split(',');
        const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
      };

      const createCustomizeURL = (url: string) => {
        let customURL = url;
        let params = [];
        if (utmSource) {
          params.push(`utm_source=${utmSource.replace(/ /g, '_')}`);
        }
        if (utmMedium) {
          params.push(`utm_medium=${utmMedium.replace(/ /g, '_')}`);
        }
        if (utmCampaignName) {
          params.push(`utm_campaign=${utmCampaignName.replace(/ /g, '_')}`);
        }

        if (customUtms[utms[0]]?.label) {
          const orignalField = `{{${customUtms[utms[0]]?.label.replace('utm_', '').replace('_', '.').toUpperCase()}}}`;
          const defaultValue = allFields.find(
            (field) => field.key === orignalField
          )?.defaultValue;
          const attachUtmKeys = appendUtmParameters(orignalField, defaultValue);
          const mergeUtmParams =
            attachUtmKeys && orignalField == '{{C.PHONE_NUMBER}}'
              ? encodeURIComponent(
                `${customUtms[utms[0]]?.label}=${defaultValue}&${attachUtmKeys}`
              ).replace(/[\s\(\)]/g, '')
              : attachUtmKeys
                ? `${customUtms[utms[0]]?.label}=${defaultValue}&${attachUtmKeys}`
                : `${customUtms[utms[0]]?.label}=${defaultValue}`;
          params.push(mergeUtmParams);
        }

        if (customUtms[utms[1]]?.label) {
          const orignalField = `{{${customUtms[utms[1]]?.label.replace('utm_', '').replace('_', '.').toUpperCase()}}}`;
          const defaultValue = allFields.find(
            (field) => field.key === orignalField
          )?.defaultValue;
          const attachUtmKeys = appendUtmParameters(orignalField, defaultValue);
          const mergeUtmParams =
            attachUtmKeys && orignalField == '{{C.PHONE_NUMBER}}'
              ? encodeURIComponent(
                `${customUtms[utms[1]]?.label}=${defaultValue}&${attachUtmKeys}`
              ).replace(/[\s\(\)]/g, '')
              : attachUtmKeys
                ? `${customUtms[utms[1]]?.label}=${defaultValue}&${attachUtmKeys}`
                : `${customUtms[utms[1]]?.label}=${defaultValue}`;
          params.push(mergeUtmParams);
        }

        if (customUtms[utms[2]]?.label) {
          const orignalField = `{{${customUtms[utms[2]]?.label.replace('utm_', '').replace('_', '.').toUpperCase()}}}`;
          const defaultValue = allFields.find(
            (field) => field.key === orignalField
          )?.defaultValue;
          const attachUtmKeys = appendUtmParameters(orignalField, defaultValue);
          const mergeUtmParams =
            attachUtmKeys && orignalField == '{{C.PHONE_NUMBER}}'
              ? encodeURIComponent(
                `${customUtms[utms[2]]?.label}=${defaultValue}&${attachUtmKeys}`
              ).replace(/[\s\(\)]/g, '')
              : attachUtmKeys
                ? `${customUtms[utms[2]]?.label}=${defaultValue}&${attachUtmKeys}`
                : `${customUtms[utms[2]]?.label}=${defaultValue}`;
          params.push(mergeUtmParams);
        }

        if (params.length > 0) {
          customURL += `/?${params.join('&')}`;
        }

        return encodeURI(customURL);
      };

      const addNewQRCode = async () => {
        if (utmCampaignName) {
          if (url) {
            if (validURL(url) && !containsDisallowedDomains(url)) {
              const isValidQR = validateQRCode();
              if (!isValidQR) return false;

              setSaveQrLoading(true);
              const randomizedId = Math.random().toString(36).substring(2, 7);
              const customQRUrl = createCustomizeURL(url);

              const src = await getQR(customQRUrl);
              if (currentTheme === 'v2' && onUploadQRCode && onGetQRCodes) {
                const file = base64ToFile(src, `${utmCampaignName}-qr.png`);
                const uploadedFile = await uploadFile(file);
                if (!uploadedFile) {
                  setSaveQrLoading(false);
                  return dispatch(failure(MESSAGES.TEMPLATE.QR_SECTION.FAILED_QR));
                }
                const notificationPayload = getQrNotificationPayload();
                const payload = {
                  name: utmCampaignName,
                  ...notificationPayload,
                  qrCodeJson: {
                    name: utmCampaignName,
                    url: url,
                    qrImagePath: uploadedFile,
                    utm_source: utmSource,
                    utm_medium: utmMedium,
                    utm_campaign_name: utmCampaignName,
                    custom_utms: customUtms,
                    ...notificationPayload,
                  },
                };

                try {
                  await onUploadQRCode(payload);
                  // Reset pagination and reload from first page
                  setCurrentPage(1);
                  setHasMore(true);
                  await loadQRCodes(1, false, false);
                  handleDialogClose(); // Close modal
                  clearQRFields();
                  setSaveQrLoading(false);
                } catch (error) {
                  setSaveQrLoading(false);
                  console.error('Failed to create QR code:', error);
                }
              } else {
                store.activePage.addElement({
                  id: `qr-${randomizedId}`,
                  type: 'image',
                  name: 'qr',
                  x: 50,
                  y: 50,
                  width: 100,
                  height: 100,
                  blurRadius: 0,
                  keepRatio: true,
                  src,
                  custom: {
                    url,
                    utm_source: utmSource,
                    utm_medium: utmMedium,
                    utm_campaign_name: utmCampaignName,
                    custom_utms: customUtms,
                    ...getQrNotificationPayload(),
                  },
                });
                clearQRFields();
              }
              setSaveQrLoading(false);
            } else {
              dispatch(failure(MESSAGES.TEMPLATE.QR_SECTION.INVALID_URL));
            }
          } else {
            dispatch(failure(MESSAGES.TEMPLATE.QR_SECTION.EMPTY_QR));
          }
        } else {
          dispatch(failure(MESSAGES.TEMPLATE.QR_SECTION.EMPTY_CAMPAIGN));
        }
      };

      // V2 API Functions
      const loadQRCodes = React.useCallback(
        async (page: number = 1, append: boolean = false, loading = true) => {
          if (!onGetQRCodes) return;

          if (isLoadingRef.current) return;
          isLoadingRef.current = true;

          if (page === 1 && loading) {
            setIsLoading(true);
          } else {
            setIsLoadingMore(true);
          }

          try {
            const payload = {
              page,
              pageSize: 15,
            };

            const qrCodesData = await onGetQRCodes(payload);

            if (qrCodesData && Array.isArray(qrCodesData.rows)) {
              const normalized = qrCodesData.rows.map((qr: any) => ({
                id: qr.id,
                name: qr?.qrCodeJson?.name || qr?.qrCodeJson?.utm_campaign_name || '',
                url: qr.qrCodeJson.url,
                qrImagePath: qr.qrCodeJson.qrImagePath,
                status: qr.status,
                utm_source: qr.qrCodeJson.utm_source,
                utm_medium: qr.qrCodeJson.utm_medium,
                utm_campaign_name: qr.qrCodeJson.utm_campaign_name,
                custom_utms: qr.qrCodeJson.custom_utms,
                sendEmail: qr.sendEmail ?? qr.qrCodeJson.sendEmail ?? false,
                notificationEmail: qr.notificationEmail ?? qr.qrCodeJson.notificationEmail ?? '',
              }));

              if (append && page > 1) {
                setQrCodes((prevQrCodes) => {
                  const existingIds = new Set(
                    prevQrCodes.map((qr: any) => qr.id)
                  );
                  const newQrCodes = normalized.filter(
                    (qr: any) => !existingIds.has(qr.id)
                  );
                  return [...prevQrCodes, ...newQrCodes];
                });
              } else {
                setQrCodes(normalized);
              }

              const hasMorePages =
                qrCodesData.currentPage < qrCodesData.lastPage;
              setHasMore(hasMorePages);
              setCurrentPage(qrCodesData.currentPage);
            } else {
              if (!append) {
                setQrCodes([]);
              }
              setHasMore(false);
            }
          } catch (error) {
            console.error('Failed to load QR codes:', error);
          } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
            isLoadingRef.current = false;
          }
        },
        [onGetQRCodes]
      );

      const loadMoreQRCodes = React.useCallback(async () => {
        if (!hasMore || isLoadingMore || isLoading) return;
        await loadQRCodes(currentPage + 1, true);
      }, [hasMore, isLoadingMore, isLoading, currentPage, loadQRCodes]);

      const handleScroll = React.useCallback(() => {
        if (!qrCodesGridRef.current || !hasMore || isLoadingMore || isLoading)
          return;

        const { scrollTop, scrollHeight, clientHeight } = qrCodesGridRef.current;
        const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

        if (scrollPercentage > 0.75) {
          if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
          }

          loadingTimeoutRef.current = setTimeout(() => {
            loadMoreQRCodes();
          }, 300);
        }
      }, [hasMore, isLoadingMore, isLoading, loadMoreQRCodes]);

      const handleQRCodeSelect = async (qrCode: any) => {
        let customQRUrl = '';
        let src: any = '';
        let customData = {};

        if (currentTheme === 'v2') {
          customQRUrl = createCustomizeURL(qrCode.url);
          src = qrCode.qrImagePath;
          customData = {
            url: qrCode.url,
            qrCodeInstanceId: qrCode.id,
            qrImagePath: qrCode.qrImagePath,
            utm_source: qrCode.utm_source,
            utm_medium: qrCode.utm_medium,
            utm_campaign_name: qrCode.utm_campaign_name,
            custom_utms: qrCode.custom_utms,
            ...(showQrScanNotificationFields
              ? {
                sendEmail: qrScanNotificationEnabled && Boolean(qrCode.sendEmail),
                notificationEmail: qrScanNotificationEnabled ? qrCode.notificationEmail : null,
              }
              : {}),
          }
        } else {
          customQRUrl = createCustomizeURL(qrCode.url);
          src = await getQR(customQRUrl);
          customData = {
            url: qrCode.url,
            utm_source: qrCode.utmSource,
            utm_medium: qrCode.utmMedium,
            utm_campaign_name: qrCode.utmCampaign,
            custom_utms: qrCode.customUtms,
          }
        }

        const randomizedId = Math.random().toString(36).substring(2, 7);
        store.activePage.addElement({
          id: `qr-${randomizedId}`,
          type: 'image',
          name: 'qr',
          x: 50,
          y: 50,
          width: 100,
          height: 100,
          blurRadius: 0,
          keepRatio: true,
          src,
          custom: customData,
        });
      };

      const handleDeleteQRCode = async (qrCodeId: string | number) => {
        if (!onDeleteQRCodes) return;
        setIsLoadingDelete(true);
        try {
          await onDeleteQRCodes(qrCodeId);
          setQrCodes((prev) => prev.filter((qr) => qr.id !== qrCodeId));
          setActiveDropdown(null);
          setIsActionsOpen(null);
        } catch (error) {
          console.error('Failed to delete QR code:', error);
        }
        finally {
          setIsLoadingDelete(false);
        }
      };

      const handleEditQRCode = async (qrCode: any) => {
        setIsEditing(true);
        setSelectedQR(qrCode);
        dispatch(setQrUrl(qrCode.url ?? ''));
        dispatch(setUtmSource(qrCode.utm_source ?? ''));
        dispatch(setUtmMedium(qrCode.utm_medium ?? ''));
        dispatch(setUtmCampaignName(qrCode.utm_campaign_name ?? ''));
        dispatch(setQrSendEmail(qrScanNotificationEnabled && Boolean(qrCode.sendEmail)));
        dispatch(
          setQrNotificationEmail(
            qrScanNotificationEnabled ? qrCode.notificationEmail ?? defaultQrNotificationEmail : ''
          )
        );
        dispatch(setCustomUtms(qrCode.custom_utms ?? {}));

        handleDialogChange('qr-modal', false);
        setIsActionsOpen(null);
      };

      const toggleActions = (
        qrCodeId: string | number,
        event: React.MouseEvent
      ) => {
        event.stopPropagation();
        const isOpening = isActionsOpen !== qrCodeId;
        if (isOpening) {
          const triggerEl = actionsRefs.current.get(qrCodeId);
          const scrollContainer = qrCodesGridRef.current;
          const dropdownHeight = 80;
          if (triggerEl && scrollContainer) {
            const triggerRect = triggerEl.getBoundingClientRect();
            const containerRect = scrollContainer.getBoundingClientRect();
            const spaceBelow = containerRect.bottom - triggerRect.bottom;
            setActionsDropdownUpward(spaceBelow < dropdownHeight);
          } else {
            setActionsDropdownUpward(false);
          }
        }
        setIsActionsOpen(isActionsOpen === qrCodeId ? null : qrCodeId);
      };

      // if selection is changed we need to update input value
      const updateQRCode = async () => {
        if (url) {
          if (validURL(url) && !containsDisallowedDomains(url)) {
            const isValidQR = validateQRCode();
            if (!isValidQR) return false;

            setSaveQrLoading(true);
            const customQRUrl = createCustomizeURL(url);
            const src = await getQR(customQRUrl);

            if (currentTheme === 'v2' && onEditQRCode && onGetQRCodes) {
              const file = base64ToFile(src, `${utmCampaignName}-qr.png`);
              const uploadedFile = await uploadFile(file);
              const notificationPayload = getQrNotificationPayload(selectedQR);
              const payload = {
                name: utmCampaignName,
                id: selectedQR?.id,
                ...notificationPayload,
                qrCodeJson: {
                  name: utmCampaignName,
                  url: url,
                  qrImagePath: uploadedFile,
                  utm_source: utmSource || null,
                  utm_medium: utmMedium || null,
                  utm_campaign_name: utmCampaignName,
                  custom_utms: customUtms,
                  ...notificationPayload,
                },
              };

              try {
                await onEditQRCode(payload);
                // Reset pagination and reload from first page
                setCurrentPage(1);
                setHasMore(true);
                await loadQRCodes(1, false, false);
                handleDialogClose(); // Close modal
                clearQRFields();
                setSaveQrLoading(false);
              } catch (error) {
                setSaveQrLoading(false);
                console.error('Failed to create QR code:', error);
              }
            } else if (el?.name === 'qr' && url) {
              el.set({
                src,
                custom: {
                  url,
                  utm_source: utmSource,
                  utm_medium: utmMedium,
                  utm_campaign_name: utmCampaignName,
                  custom_utms: customUtms,
                  ...getQrNotificationPayload(el?.custom),
                },
              });
              clearQRFields();
              setSaveQrLoading(false);
            }
          } else {
            dispatch(failure(MESSAGES.TEMPLATE.QR_SECTION.INVALID_URL));
          }
        } else {
          dispatch(failure(MESSAGES.TEMPLATE.QR_SECTION.EMPTY_QR));
        }
      };

      // Handler to update dropdown values
      const handleSelect = (utmKey: string, value: any) => {
        const updatedUtms = { ...customUtms };
        if (value === null) {
          delete updatedUtms[utmKey];
        } else {
          updatedUtms[utmKey] = value;
        }
        dispatch(setCustomUtms(updatedUtms));
      };

      const getTrackingPhoneValue = (phoneRecord: any) =>
        phoneRecord?.phoneNumber ||
        phoneRecord?.trackingPhoneNumber ||
        phoneRecord?.trackingNumber ||
        phoneRecord?.number ||
        phoneRecord?.value ||
        '';

      const getTrackingPhoneId = (phoneRecord: any) =>
        phoneRecord?.trackingPhoneNumberId ||
        phoneRecord?.tracking_phone_number_id ||
        phoneRecord?.trackingNumberId ||
        phoneRecord?.id ||
        getTrackingPhoneValue(phoneRecord);

      const getTrackingPhoneCopyValue = (phoneRecord: any) =>
        getTrackingPhoneToken(getTrackingPhoneValue(phoneRecord));

      const registerTrackingPhoneRecord = (phoneRecord: any) => {
        registerTrackingPhoneToken({
          trackingPhoneNumberId: getTrackingPhoneId(phoneRecord),
          phoneNumber: getTrackingPhoneValue(phoneRecord),
        });
      };

      const normalizeTrackingPhoneResponse = (phoneNumbersData: any) => {
        const phoneNumbersPayload =
          phoneNumbersData?.data?.data ||
          phoneNumbersData?.data ||
          phoneNumbersData;
        const rows = Array.isArray(phoneNumbersPayload?.rows)
          ? phoneNumbersPayload.rows
          : Array.isArray(phoneNumbersPayload)
            ? phoneNumbersPayload
            : Array.isArray(phoneNumbersData?.rows)
              ? phoneNumbersData.rows
              : Array.isArray(phoneNumbersData)
                ? phoneNumbersData
                : [];

        return {
          rows,
          currentPage:
            phoneNumbersPayload?.currentPage ||
            phoneNumbersData?.currentPage ||
            1,
          lastPage:
            phoneNumbersPayload?.lastPage ||
            phoneNumbersData?.lastPage ||
            1,
        };
      };

      const loadTrackingPhoneNumbers = React.useCallback(
        async (page: number = 1, append: boolean = false, loading = true) => {
          if (!onGetTrackingPhoneNumbers) return;

          if (isPhoneLoadingRef.current) return;
          isPhoneLoadingRef.current = true;

          if (page === 1 && loading) {
            setIsPhoneLoading(true);
          } else {
            setIsPhoneLoadingMore(true);
          }

          try {
            const phoneNumbersData = normalizeTrackingPhoneResponse(
              await onGetTrackingPhoneNumbers({
                page,
                pageSize: 15,
              })
            );

            const normalized = phoneNumbersData.rows.map((phoneNumber: any) => {
              const trackingPhoneNumberId = getTrackingPhoneId(phoneNumber);
              const normalizedPhoneNumber = {
                ...phoneNumber,
                id: trackingPhoneNumberId,
                trackingPhoneNumberId,
                name: phoneNumber.name || phoneNumber.title || 'Tracking Number',
                phoneNumber: getTrackingPhoneValue(phoneNumber),
                status: phoneNumber.status || '',
              };
              registerTrackingPhoneRecord(normalizedPhoneNumber);
              return normalizedPhoneNumber;
            });

            if (append && page > 1) {
              setTrackingPhoneNumbers((prevPhoneNumbers) => {
                const existingIds = new Set(
                  prevPhoneNumbers.map((phoneNumber: any) => phoneNumber.id)
                );
                const newPhoneNumbers = normalized.filter(
                  (phoneNumber: any) => !existingIds.has(phoneNumber.id)
                );
                return [...prevPhoneNumbers, ...newPhoneNumbers];
              });
            } else {
              setTrackingPhoneNumbers(normalized);
            }

            setPhoneHasMore(phoneNumbersData.currentPage < phoneNumbersData.lastPage);
            setPhoneCurrentPage(phoneNumbersData.currentPage);
          } catch (error) {
            console.error('Failed to load tracking phone numbers:', error);
          } finally {
            setIsPhoneLoading(false);
            setIsPhoneLoadingMore(false);
            isPhoneLoadingRef.current = false;
          }
        },
        [onGetTrackingPhoneNumbers]
      );

      const loadMoreTrackingPhoneNumbers = React.useCallback(async () => {
        if (!phoneHasMore || isPhoneLoadingMore || isPhoneLoading) return;
        await loadTrackingPhoneNumbers(phoneCurrentPage + 1, true);
      }, [
        phoneHasMore,
        isPhoneLoadingMore,
        isPhoneLoading,
        phoneCurrentPage,
        loadTrackingPhoneNumbers,
      ]);

      const handleTrackingPhoneScroll = React.useCallback(() => {
        if (
          !trackingPhoneNumbersRef.current ||
          !phoneHasMore ||
          isPhoneLoadingMore ||
          isPhoneLoading
        ) {
          return;
        }

        const { scrollTop, scrollHeight, clientHeight } = trackingPhoneNumbersRef.current;
        const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

        if (scrollPercentage > 0.75) {
          if (phoneLoadingTimeoutRef.current) {
            clearTimeout(phoneLoadingTimeoutRef.current);
          }

          phoneLoadingTimeoutRef.current = setTimeout(() => {
            loadMoreTrackingPhoneNumbers();
          }, 300);
        }
      }, [
        phoneHasMore,
        isPhoneLoadingMore,
        isPhoneLoading,
        loadMoreTrackingPhoneNumbers,
      ]);

      const addTrackingPhoneNumberToTemplate = (phoneRecord: any) => {
        registerTrackingPhoneRecord(phoneRecord);
        const trackingPhoneCopyValue = getTrackingPhoneCopyValue(phoneRecord);
        const trackingPhoneRawValue = getRawTrackingPhoneToken(getTrackingPhoneValue(phoneRecord));
        const trackingPhoneNumberId = getTrackingPhoneId(phoneRecord);
        const randomizedId = Math.random().toString(36).substring(2, 7);

        store.activePage.addElement({
          id: `tracking-phone-${randomizedId}`,
          type: 'text',
          name: 'tracking-phone',
          x: 50,
          y: 50,
          text: trackingPhoneCopyValue,
          width: 150,
          contentEditable: false,
          custom: {
            trackingPhoneNumberId,
            phoneNumber: getTrackingPhoneValue(phoneRecord),
            value: trackingPhoneRawValue,
          },
        });

        dispatch(success(`${phoneRecord?.name || 'Tracking Number'} Added`));
      };

      const copyTrackingPhoneNumber = (phoneRecord: any) => {
        const trackingPhoneNumber = getTrackingPhoneValue(phoneRecord);
        registerTrackingPhoneRecord(phoneRecord);
        copyToClipboard(getTrackingPhoneCopyValue(phoneRecord));
        dispatch(success(`${formatTrackingPhoneNumber(trackingPhoneNumber)} Copied`));
      };

      const resetTrackingPhoneForm = () => {
        setTrackingPhoneForm({
          phoneName: '',
          callForwardingNumber: '',
          smsForwardingEmail: '',
          areaCode: '',
          selectedNumber: '',
        });
        setTrackingPhoneErrors({});
        setAvailableTrackingPhoneNumbers([]);
        setHasSearchedTrackingPhoneNumbers(false);
      };

      const handleTrackingPhoneDialogClose = () => {
        setIsTrackingPhoneDialogOpen(false);
        setTimeout(resetTrackingPhoneForm, 10);
      };

      const handleTrackingPhoneInputChange = (field: string, value: string) => {
        setTrackingPhoneForm((prev) => ({
          ...prev,
          [field]: value,
          ...(field === 'areaCode' ? { selectedNumber: '' } : {}),
        }));

        if (field === 'areaCode') {
          setHasSearchedTrackingPhoneNumbers(false);
          setAvailableTrackingPhoneNumbers([]);
        }

        if (trackingPhoneErrors[field]) {
          setTrackingPhoneErrors((prev) => {
            const { [field]: _removed, ...rest } = prev;
            return rest;
          });
        }
      };

      const normalizeAvailableTrackingPhoneNumbers = (lookupData: any, areaCode: string) => {
        const lookupPayload =
          lookupData?.data?.data ||
          lookupData?.data ||
          lookupData;
        const rows = Array.isArray(lookupPayload?.rows)
          ? lookupPayload.rows
          : Array.isArray(lookupPayload)
            ? lookupPayload
            : Array.isArray(lookupData?.rows)
              ? lookupData.rows
              : Array.isArray(lookupData)
                ? lookupData
                : [];

        return rows.map((number: any) => {
          const phoneNumber =
            number.phoneNumber ||
            number.phone_number ||
            number.trackingPhoneNumber ||
            number.number ||
            number.value ||
            '';

          return {
            phoneLabel:
              number.phoneLabel ||
              number.label ||
              `${formatTrackingPhoneNumber(phoneNumber)}${number.city ? ` - ${number.city}${number.state ? `, ${number.state}` : ''}` : ''}`,
            phoneNumber,
          };
        }).filter((number: any) => number.phoneNumber);
      };

      const handleFindTrackingPhoneNumbers = async () => {
        const trimmedAreaCode = trackingPhoneForm.areaCode.trim();
        if (!trimmedAreaCode) {
          setTrackingPhoneErrors((prev) => ({
            ...prev,
            areaCode: 'Area code is required.',
          }));
          return;
        }

        if (trimmedAreaCode.length < 3) {
          setTrackingPhoneErrors((prev) => ({
            ...prev,
            areaCode: 'Enter at least 3 digits.',
          }));
          return;
        }

        if (!/^\d+$/.test(trimmedAreaCode)) {
          setTrackingPhoneErrors((prev) => ({
            ...prev,
            areaCode: 'Enter a valid area code.',
          }));
          return;
        }

        setIsTrackingPhoneLookupLoading(true);
        setHasSearchedTrackingPhoneNumbers(false);
        setTrackingPhoneErrors((prev) => {
          const { areaCode, ...rest } = prev;
          return rest;
        });
        setTrackingPhoneForm((prev) => ({
          ...prev,
          areaCode: trimmedAreaCode,
          selectedNumber: '',
        }));

        try {
          if (!onLookupTrackingPhoneNumbers) {
            setAvailableTrackingPhoneNumbers([]);
            return;
          }

          const lookupResponse = await onLookupTrackingPhoneNumbers({ areaCode: trimmedAreaCode });
          if (
            lookupResponse?.status &&
            lookupResponse.status !== 200
          ) {
            dispatch(
              failure(
                lookupResponse?.data?.message ||
                'Failed to find available numbers. Please try again.'
              )
            );
            return;
          }
          const normalizedNumbers = normalizeAvailableTrackingPhoneNumbers(
            lookupResponse,
            trimmedAreaCode
          );
          setAvailableTrackingPhoneNumbers(normalizedNumbers);
          setHasSearchedTrackingPhoneNumbers(true);
        } catch (error) {
          dispatch(failure('Failed to find available numbers. Please try again.'));
        } finally {
          setIsTrackingPhoneLookupLoading(false);
        }
      };

      const validateTrackingPhoneForm = () => {
        const trimmedPhoneName = trackingPhoneForm.phoneName.trim();
        const trimmedCallForwardingNumber = trackingPhoneForm.callForwardingNumber.trim();
        const trimmedSmsForwardingEmail = trackingPhoneForm.smsForwardingEmail.trim();
        const trimmedAreaCode = trackingPhoneForm.areaCode.trim();
        const validationErrors: Record<string, string> = {};

        if (!trimmedPhoneName) {
          validationErrors.phoneName = 'Phone name is required.';
        }

        if (!trimmedCallForwardingNumber) {
          validationErrors.callForwardingNumber = 'Call forwarding number is required.';
        } else if (trimmedCallForwardingNumber.length > 20) {
          validationErrors.callForwardingNumber =
            'Call forwarding number must be less than 20 characters.';
        }

        if (
          trimmedSmsForwardingEmail &&
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedSmsForwardingEmail)
        ) {
          validationErrors.smsForwardingEmail = 'Please enter a valid email address.';
        }

        if (!trimmedAreaCode) {
          validationErrors.areaCode = 'Please enter an area code and select a phone number.';
        } else if (trimmedAreaCode.length < 3) {
          validationErrors.areaCode = 'Please enter at least 3 digits and select a phone number.';
        } else if (!/^\d+$/.test(trimmedAreaCode)) {
          validationErrors.areaCode = 'Please enter a valid area code and select a phone number.';
        } else if (!hasSearchedTrackingPhoneNumbers) {
          validationErrors.areaCode = 'Please search for available numbers first.';
        } else if (!trackingPhoneForm.selectedNumber) {
          validationErrors.areaCode = 'Please select a phone number.';
        }

        setTrackingPhoneErrors(validationErrors);

        if (Object.keys(validationErrors).length) {
          return null;
        }

        return {
          name: trimmedPhoneName,
          phoneNumber: trackingPhoneForm.selectedNumber,
          forwardingPhoneNumber: trimmedCallForwardingNumber,
          ...(trimmedSmsForwardingEmail ? { smsForwardingEmail: trimmedSmsForwardingEmail } : {}),
        };
      };

      const handleCreateTrackingPhoneNumber = async () => {
        const payload = validateTrackingPhoneForm();
        if (!payload) return;

        setIsTrackingPhoneCreateLoading(true);

        try {
          let createdTrackingPhoneNumber: any = {
            id: `local-tracking-phone-${Date.now()}`,
            name: payload.name,
            phoneNumber: payload.phoneNumber,
            forwardingPhoneNumber: payload.forwardingPhoneNumber,
            smsForwardingEmail: payload.smsForwardingEmail,
            status: 'Active',
          };

          if (onCreateTrackingPhoneNumber) {
            const response = await onCreateTrackingPhoneNumber(payload);
            if (response?.status && response.status !== 200 && response.status !== 201) {
              dispatch(
                failure(
                  response?.data?.message ||
                  'Failed to save phone tracking number. Please try again.'
                )
              );
              return;
            }
            createdTrackingPhoneNumber =
              response?.data?.data ||
              response?.data ||
              response ||
              createdTrackingPhoneNumber;
          }

          const trackingPhoneNumberId =
            getTrackingPhoneId(createdTrackingPhoneNumber) ||
            `local-tracking-phone-${Date.now()}`;

          setTrackingPhoneNumbers((prev) => [
            {
              ...createdTrackingPhoneNumber,
              id: trackingPhoneNumberId,
              trackingPhoneNumberId,
              name: createdTrackingPhoneNumber.name || payload.name,
              phoneNumber: getTrackingPhoneValue(createdTrackingPhoneNumber) || payload.phoneNumber,
              status: createdTrackingPhoneNumber.status || 'Active',
            },
            ...prev,
          ]);
          registerTrackingPhoneToken({
            trackingPhoneNumberId,
            phoneNumber: getTrackingPhoneValue(createdTrackingPhoneNumber) || payload.phoneNumber,
          });
          dispatch(success('Tracking Phone Number Created Successfully.'));
          handleTrackingPhoneDialogClose();
        } catch (error) {
          dispatch(failure('Failed to save phone tracking number. Please try again.'));
        } finally {
          setIsTrackingPhoneCreateLoading(false);
        }
      };

      // if selection is changed we need to update input value
      useEffect(() => {
        if (el?.name === 'qr' && currentTheme !== 'v2') {
          dispatch(setIsQR(el?.name === 'qr'));
          dispatch(setQrUrl(el?.custom?.url || el?.custom?.value || ''));
          dispatch(setUtmSource(el?.custom?.utm_source || 'direct mail'));
          dispatch(setUtmMedium(el?.custom?.utm_medium || 'QR Code'));
          dispatch(setUtmCampaignName(el?.custom?.utm_campaign_name || ''));
          dispatch(setQrSendEmail(qrScanNotificationEnabled && Boolean(el?.custom?.sendEmail)));
          dispatch(
            setQrNotificationEmail(
              qrScanNotificationEnabled
                ? el?.custom?.notificationEmail ?? defaultQrNotificationEmail
                : ''
            )
          );
          if (Object.values(el?.custom?.custom_utms || {}).length) {
            dispatch(setCustomUtms(el?.custom?.custom_utms));
          } else {
            dispatch(setCustomUtms({}));
          }
        } else if (isQR && el?.name !== 'qr') {
          dispatch(clearQrFields());
        }
      }, [isQR, el]);

      // Handle click outside to close actions menu
      useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
          // Close actions if clicking outside the currently open actions container
          if (isActionsOpen !== null) {
            const actionsElement = actionsRefs.current.get(isActionsOpen);
            if (
              actionsElement &&
              !actionsElement.contains(event.target as Node)
            ) {
              setIsActionsOpen(null);
            }
          }

          // V2: Handle dropdowns
          if (activeDropdown) {
            const dropdownElement = dropdownRefs.current.get(activeDropdown);
            if (
              dropdownElement &&
              !dropdownElement.contains(event.target as Node)
            ) {
              setActiveDropdown(null);
            }
          }
        };

        if (isActionsOpen !== null || activeDropdown !== null) {
          document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
        };
      }, [isActionsOpen, activeDropdown]);

      // V2: Load only the active tracking tab to avoid duplicate QR/Phone calls.
      useEffect(() => {
        if (selectedTrackingTab.id === 'qr' && onGetQRCodes && !qrCodesLoadedRef.current) {
          qrCodesLoadedRef.current = true;
          loadQRCodes(1, false);
        }
      }, [selectedTrackingTab.id, onGetQRCodes, loadQRCodes]);

      useEffect(() => {
        if (
          selectedTrackingTab.id === 'phone' &&
          onGetTrackingPhoneNumbers &&
          !trackingPhoneNumbersLoadedRef.current
        ) {
          trackingPhoneNumbersLoadedRef.current = true;
          loadTrackingPhoneNumbers(1, false);
        }
      }, [selectedTrackingTab.id, onGetTrackingPhoneNumbers, loadTrackingPhoneNumbers]);

      // V2: Setup scroll event listener on the list container
      useEffect(() => {
        const listElement = qrCodesGridRef.current;
        if (listElement && onGetQRCodes) {
          listElement.addEventListener('scroll', handleScroll);
          return () => {
            listElement.removeEventListener('scroll', handleScroll);
            if (loadingTimeoutRef.current) {
              clearTimeout(loadingTimeoutRef.current);
            }
          };
        }
      }, [
        hasMore,
        isLoadingMore,
        isLoading,
        currentPage,
        handleScroll,
        onGetQRCodes,
      ]);

      useEffect(() => {
        const listElement = trackingPhoneNumbersRef.current;
        if (listElement && onGetTrackingPhoneNumbers) {
          listElement.addEventListener('scroll', handleTrackingPhoneScroll);
          return () => {
            listElement.removeEventListener('scroll', handleTrackingPhoneScroll);
            if (phoneLoadingTimeoutRef.current) {
              clearTimeout(phoneLoadingTimeoutRef.current);
            }
          };
        }
      }, [
        phoneHasMore,
        isPhoneLoadingMore,
        isPhoneLoading,
        phoneCurrentPage,
        handleTrackingPhoneScroll,
        onGetTrackingPhoneNumbers,
      ]);

      return (
        <>
          {currentTheme === 'v2' ? (
            <>
              <div className="qr-section-v2-container tracking-section-v2-container">
                <Tabs
                  value={selectedTrackingTab}
                  onChange={handleTrackingTabChange}
                  tabs={trackingTabs}
                  className="tracking-tabs"
                  tabClassName="tracking-tab"
                  indicatorClassName="tracking-tab-indicator"
                />
                {selectedTrackingTab.id === 'qr' ? (
                  <>
                    <div
                      className="qr-code-wrapper qr-code-wrapper-sticky"
                      onClick={() => handleDialogChange('qr-modal')}
                    >
                      <QRCodeIcon />
                      <button className="qr-submit-btn-new">Create QR-Code</button>
                    </div>
                    <Typography variant="h6" style={noteStyles}>
                      {MESSAGES.TEMPLATE.QR_SECTION.QR_NOTE}
                    </Typography>

                    {/* V2 QR Codes List when callbacks are provided */}
                    {onGetQRCodes && (
                      <div
                        className="qr-codes-list-scroll"
                        ref={qrCodesGridRef}
                      >
                        <div className="qr-codes-grid">
                        {isLoading ? (
                          <div className="loading-state">Loading QR codes...</div>
                        ) : qrCodes.length === 0 ? (
                          <div className="empty-state">No QR codes created yet</div>
                        ) : (
                          <>
                            {qrCodes.map((qrCode) => (
                              <div key={qrCode.id} className="qr-card" onClick={() => handleQRCodeSelect(qrCode)}>
                                <img
                                  src={qrCode.qrImagePath}
                                  alt="code"
                                  style={{ width: '30px', height: '30px' }}
                                />
                                <div
                                  className="qr-card-content"
                                >
                                  <Typography
                                    style={{
                                      fontWeight: '600',
                                      fontSize: '12px',
                                      color: '#545454',
                                      margin: 0,
                                    }}
                                  >
                                    {qrCode.name}
                                  </Typography>
                                  <Typography
                                    style={{
                                      fontWeight: '400',
                                      fontSize: '12px',
                                      color: '#54545499',
                                      margin: 0,
                                    }}
                                  >
                                    {qrCode.status}
                                  </Typography>
                                </div>
                                {(onEditQRCode || onDeleteQRCodes) && <div
                                  className="actions-container"
                                  ref={(el) => {
                                    if (el) actionsRefs.current.set(qrCode.id, el);
                                  }}
                                >
                                  <div
                                    className="actions"
                                    onClick={(e) => { toggleActions(qrCode.id, e); e.stopPropagation(); }}
                                  >
                                    <img src={Actions} alt='actions' />
                                  </div>
                                  {/* Actions */}
                                  {isActionsOpen === qrCode.id && (
                                    <div
                                      className={`actions-wrapper${actionsDropdownUpward ? ' actions-wrapper-up' : ''}`}
                                    >
                                      {onEditQRCode && (
                                        <div
                                          className="action-item"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditQRCode(qrCode);
                                          }}
                                        >
                                          <img src={Edit} alt="edit" />
                                          <Typography style={itemStyle}>
                                            Edit
                                          </Typography>
                                        </div>
                                      )}
                                      {onDeleteQRCodes && (
                                        <div
                                          className="action-item"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenDeleteDialog();
                                            setSelectedQR(qrCode.id);
                                          }}
                                        >
                                          <img src={Archive} alt="Archive" />
                                          <Typography style={itemStyle}>
                                            Archive
                                          </Typography>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>}
                              </div>
                            ))}
                            {/* Loading indicator for pagination */}
                            {isLoadingMore && (
                              <div className="loading-more">
                                <div className="loading-state">
                                  Loading more QR codes...
                                </div>
                              </div>
                            )}
                          </>
                        )}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div
                      className="qr-code-wrapper qr-code-wrapper-sticky"
                      onClick={() => setIsTrackingPhoneDialogOpen(true)}
                    >
                      <TrackingPhoneIcon />
                      <button className="qr-submit-btn-new">Activate Tracking Phone Number</button>
                    </div>
                    <Typography variant="h6" style={trackingPhoneNoteStyles}>
                      Add a Phone Tracking Engagement. Select a saved phone tracking or create a new one for this template.
                    </Typography>
                    <div
                      className="qr-codes-list-scroll tracking-phone-list-scroll"
                      ref={trackingPhoneNumbersRef}
                    >
                      <div className="qr-codes-grid">
                        {isPhoneLoading ? (
                          <div className="loading-state">Loading tracking numbers...</div>
                        ) : trackingPhoneNumbers.length === 0 ? (
                          <div className="empty-state">No tracking numbers created yet</div>
                        ) : (
                          <>
                            {trackingPhoneNumbers.map((phoneRecord) => (
                              <div
                                style={{ display: 'flex', alignItems: 'center' }}
                                key={phoneRecord.id}
                              >
                                <span
                                  className="contact-element tracking-phone-element"
                                  onClick={() => addTrackingPhoneNumberToTemplate(phoneRecord)}
                                >
                                  {formatTrackingPhoneNumber(phoneRecord.phoneNumber)}
                                </span>
                                <Button
                                  style={iconButtonStyles}
                                  onClick={(event: React.MouseEvent) => {
                                    event.stopPropagation();
                                    copyTrackingPhoneNumber(phoneRecord);
                                  }}
                                  backdrop={false}
                                >
                                  <ContentCopyIcon className="copy tracking-phone-copy-icon" />
                                </Button>
                              </div>
                            ))}
                            {isPhoneLoadingMore && (
                              <div className="loading-more">
                                <div className="loading-state">
                                  Loading more tracking numbers...
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
              {dialog.open && (
                <QRCodeModal
                  show={dialog.open}
                  utms={utms}
                  utmFields={utmFields}
                  isEditing={isEditing}
                  handleSelect={handleSelect}
                  handleDialogChange={handleDialogChange}
                  handleClose={handleDialogClose}
                  setUtmCampaignName={setUtmCampaignName}
                  setQrUrl={setQrUrl}
                  setUtmSource={setUtmSource}
                  setUtmMedium={setUtmMedium}
                  setQrSendEmail={setQrSendEmail}
                  setQrNotificationEmail={setQrNotificationEmail}
                  showQrScanNotificationFields={showQrScanNotificationFields}
                  defaultQrNotificationEmail={defaultQrNotificationEmail}
                  qrScanNotificationEnabled={qrScanNotificationEnabled}
                  setCustomUtms={setCustomUtms}
                  addNewQRCode={isEditing ? updateQRCode : addNewQRCode}
                  loading={saveQrLoading}
                />
              )}
              {isTrackingPhoneDialogOpen && (
                <div
                  className="tracking-phone-modal"
                  style={{ display: isTrackingPhoneDialogOpen ? 'flex' : 'none' }}
                >
                  <div className="tracking-phone-modal-content">
                    <div className="tracking-phone-modal-header">
                      <Typography variant="p" className="tracking-phone-modal-title">
                        Activate Tracking Phone Number
                      </Typography>
                      <button
                        type="button"
                        className="tracking-phone-modal-close"
                        onClick={handleTrackingPhoneDialogClose}
                        aria-label="Close"
                      >
                        <ConfirmCloseIcon fill="#111827" />
                      </button>
                    </div>

                    <div className="tracking-phone-modal-body">
                      <div className="tracking-phone-modal-field">
                        <label>
                          Phone Name <span>*</span>
                        </label>
                        <input
                          type="text"
                          value={trackingPhoneForm.phoneName}
                          onChange={(e) => handleTrackingPhoneInputChange('phoneName', e.target.value)}
                          placeholder="Enter Phone Name"
                          className={trackingPhoneErrors.phoneName ? 'tracking-phone-input-error' : ''}
                        />
                        {trackingPhoneErrors.phoneName && (
                          <p className="tracking-phone-error">{trackingPhoneErrors.phoneName}</p>
                        )}
                      </div>

                      <div className="tracking-phone-modal-field">
                        <label>
                          Call Forwarding Number <span>*</span>
                        </label>
                        <input
                          type="text"
                          inputMode="tel"
                          value={trackingPhoneForm.callForwardingNumber}
                          onChange={(e) => handleTrackingPhoneInputChange('callForwardingNumber', e.target.value)}
                          placeholder="Enter Forwarding Phone Number"
                          className={trackingPhoneErrors.callForwardingNumber ? 'tracking-phone-input-error' : ''}
                        />
                        <p className="tracking-phone-helper">
                          The phone number where incoming calls from this tracking number will be forwarded.
                        </p>
                        {trackingPhoneErrors.callForwardingNumber && (
                          <p className="tracking-phone-error">{trackingPhoneErrors.callForwardingNumber}</p>
                        )}
                      </div>

                      <div className="tracking-phone-modal-field">
                        <label>SMS Email Forwarding</label>
                        <input
                          type="email"
                          value={trackingPhoneForm.smsForwardingEmail}
                          onChange={(e) => handleTrackingPhoneInputChange('smsForwardingEmail', e.target.value)}
                          placeholder="Enter Email Address"
                          className={trackingPhoneErrors.smsForwardingEmail ? 'tracking-phone-input-error' : ''}
                        />
                        <p className="tracking-phone-helper">
                          The email address that will receive any text messages sent to this tracking number.
                        </p>
                        {trackingPhoneErrors.smsForwardingEmail && (
                          <p className="tracking-phone-error">{trackingPhoneErrors.smsForwardingEmail}</p>
                        )}
                      </div>

                      <div className="tracking-phone-modal-field">
                        <label>
                          Enter an Area Code <span>*</span>
                        </label>
                        <p className="tracking-phone-helper tracking-phone-area-code-helper">
                          Enter your preferred area code for the tracking number. If it's unavailable, we'll automatically assign the closest available area code based on location.
                        </p>
                        <div className="tracking-phone-area-code-row">
                          <input
                            type="text"
                            inputMode="numeric"
                            value={trackingPhoneForm.areaCode}
                            onChange={(e) => handleTrackingPhoneInputChange('areaCode', e.target.value)}
                            placeholder="Enter Area Code"
                            className={trackingPhoneErrors.areaCode ? 'tracking-phone-input-error' : ''}
                          />
                          <button
                            type="button"
                            className="tracking-phone-find-button"
                            onClick={handleFindTrackingPhoneNumbers}
                            disabled={isTrackingPhoneLookupLoading || !canFindTrackingPhoneNumbers}
                          >
                            <SearchIcon fill="#fff" />
                            {isTrackingPhoneLookupLoading ? 'Finding...' : 'Find Available Numbers'}
                          </button>
                        </div>
                        {trackingPhoneErrors.areaCode && (
                          <p className="tracking-phone-error">{trackingPhoneErrors.areaCode}</p>
                        )}
                      </div>

                      {availableTrackingPhoneNumbers.length > 0 && (
                        <div
                          className="tracking-phone-modal-field"
                          ref={availableTrackingPhoneNumbersRef}
                        >
                          <label>
                            Select a phone number <span>*</span>
                          </label>
                          <div className="tracking-phone-options">
                            {availableTrackingPhoneNumbers.map(({ phoneLabel, phoneNumber }, index) => (
                              <label className="tracking-phone-option" key={`${phoneNumber}-${index}`}>
                                <input
                                  type="radio"
                                  name="tracking-phone-number"
                                  value={phoneNumber}
                                  checked={trackingPhoneForm.selectedNumber === phoneNumber}
                                  onChange={(e) => handleTrackingPhoneInputChange('selectedNumber', e.target.value)}
                                />
                                <span>{phoneLabel}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                      {availableTrackingPhoneNumbers.length > 0 && (
                        <p className="tracking-phone-cost-note">
                          Each additional call tracking number beyond your plan's limit will add $5 to your monthly subscription.
                        </p>
                      )}
                    </div>

                    <div className="tracking-phone-modal-footer">
                      <button
                        type="button"
                        className="tracking-phone-cancel-button"
                        onClick={handleTrackingPhoneDialogClose}
                        disabled={isTrackingPhoneCreateLoading}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="tracking-phone-activate-button"
                        onClick={handleCreateTrackingPhoneNumber}
                        disabled={isTrackingPhoneCreateLoading}
                      >
                        <svg
                          className="tracking-phone-power-icon"
                          width="18"
                          height="18"
                          viewBox="0 0 18 18"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M9 1.5V8.25"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                          <path
                            d="M5.25 3.75C3.45 4.89 2.25 6.9 2.25 9.19C2.25 12.92 5.27 15.94 9 15.94C12.73 15.94 15.75 12.92 15.75 9.19C15.75 6.9 14.55 4.89 12.75 3.75"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                        {isTrackingPhoneCreateLoading ? 'Activating...' : 'Activate Tracking Number'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {openDeleteDialog && (
                <DialogV2
                  icon={<ConfirmCloseIcon fill='var(--primary-color)' />}
                  customStyles={cancelDialogStylesV2}
                  open={openDeleteDialog}
                  handleClose={handleCloseDeleteDialog}
                  title='Delete QR Code'
                  subHeading=''
                  description='Are you sure you want to delete this QR code?'
                  onSubmit={() => {
                    handleDeleteQRCode(selectedQR);
                    handleCloseDeleteDialog();
                  }}
                  onCancel={handleCloseDeleteDialog}
                  cancelText='No'
                  submitText='Yes'
                  isGallery={false}
                  loading={isLoadingDelete}
                />)}
            </>
          ) : (
            <>
              <button
                className="qr-submit-btn"
                onClick={isQR ? updateQRCode : addNewQRCode}
              >
                {isQR
                  ? MESSAGES.TEMPLATE.QR_SECTION.UPDATE_BUTTON
                  : MESSAGES.TEMPLATE.QR_SECTION.SUBMIT_BUTTON}
              </button>
              <div className="qr-input-wrapper">
                <label>QR URL*:</label>
                <Input
                  type="text"
                  onChange={(e) => {
                    dispatch(setQrUrl(e.target.value));
                  }}
                  placeholder={MESSAGES.TEMPLATE.QR_SECTION.QR_PLACEHOLDER}
                  value={url}
                  qrField={true}
                />
              </div>
              <div className="qr-input-wrapper">
                <label>UTM Source:</label>
                <Input
                  type="text"
                  onChange={(e) => {
                    dispatch(setUtmSource(e.target.value));
                  }}
                  placeholder={'Enter UTM Source'}
                  value={utmSource}
                  qrField={true}
                />
              </div>
              <div className="qr-input-wrapper">
                <label>UTM Medium:</label>
                <Input
                  type="text"
                  onChange={(e) => {
                    dispatch(setUtmMedium(e.target.value));
                  }}
                  placeholder={'Enter UTM Medium'}
                  value={utmMedium}
                  qrField={true}
                />
              </div>
              <div className="qr-input-wrapper">
                <label>UTM Campaign Name*:</label>
                <Input
                  type="text"
                  onChange={(e) => {
                    dispatch(setUtmCampaignName(e.target.value));
                  }}
                  placeholder={'Enter UTM Campaign Name'}
                  value={utmCampaignName}
                  qrField={true}
                />
              </div>
              {showQrScanNotificationFields && (
                <>
                  <label
                    className={`qr-notification-toggle${
                      qrScanNotificationEnabled
                        ? ''
                        : ' qr-notification-toggle-disabled qr-notification-toggle-disabled-inline'
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleQrScanNotificationToggle();
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={qrScanNotificationEnabled && sendEmail}
                      disabled={!qrScanNotificationEnabled}
                      readOnly
                    />
                    <span className="qr-notification-icon" aria-hidden="true">
                      <CheckboxIcon checked={qrScanNotificationEnabled && sendEmail} />
                    </span>
                    <span className="qr-notification-label">
                      <span className="qr-notification-label-text">
                        Send scan notification email
                      </span>
                      {!qrScanNotificationEnabled && (
                        <InfoIconFill
                          fill="var(--primary-color)"
                          className="qr-notification-info-icon qr-notification-info-icon-inline"
                        />
                      )}
                    </span>
                  </label>
                  {!qrScanNotificationEnabled && (
                    <GeneralTooltip
                      anchorSelect=".qr-notification-info-icon-inline"
                      place="bottom"
                      title={MESSAGES.TEMPLATE.QR_SECTION.NOTIFICATION_EMAIL_UPGRADE}
                      className="qr-notification-upgrade-tooltip"
                    />
                  )}
                  {qrScanNotificationEnabled && sendEmail && (
                    <div className="qr-input-wrapper">
                      <label>Notification Email*:</label>
                      <Input
                        type="email"
                        onChange={(e) => {
                          dispatch(setQrNotificationEmail(e.target.value));
                        }}
                        placeholder="Enter notification email"
                        value={notificationEmail}
                        qrField={true}
                      />
                    </div>
                  )}
                </>
              )}
              {utms?.map((utm, idx) => {
                return (
                  <div className="qr-input-wrapper" key={idx}>
                    <label>{utm.toUpperCase().replace(/\_/g, ' ')}:</label>
                    <GeneralSelect
                      placeholder={`Search / Select Custom UTM ${idx + 1}`}
                      options={utmFields as any}
                      setSelectedValue={(value: any) =>
                        handleSelect(utm, value)
                      }
                      selectedValue={customUtms[utm] || (null as any)}
                      builderSelect={true}
                      clearField={true}
                      search={true}
                      qrField={true}
                      isError={false}
                      gallerySelect={false}
                    />
                  </div>
                );
              })}
            </>
          )}
        </>
      );
    }
  ),
};

export default CustomQRCode;
